import { useState } from "react";
import { toast } from "sonner";
import { Briefcase, ArrowUpRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { useEntity } from "@/context/EntityContext";

/**
 * Sidebar CTA that fires the upsell from Personal → Business.
 *
 * Renders ONLY inside a Personal-edition workspace. One click opens a
 * lightweight dialog, spins up a new entity already stamped `business`,
 * and switches to it — no data migration, no cross-contamination.
 */
export function CreateBusinessCta() {
  const { currentEntity, refresh, setEntity } = useEntity();
  const isPersonal = currentEntity?.workspace_type === "personal";
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("My Business");
  const [busy, setBusy] = useState(false);

  if (!isPersonal) return null;

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error("Give the workspace a name");
    setBusy(true);
    try {
      const { data } = await api.post("/entities", {
        name: trimmed,
        kind: "business",
        icon: "💼",
        color: "#0369a1",
        workspace_type: "business",
      });
      toast.success(`Created "${trimmed}" — switching over…`);
      await refresh();
      // setEntity triggers a soft reload so the sidebar re-mounts in Business mode.
      setEntity(data.id);
    } catch (err) {
      const msg = err.response?.data?.detail || "Couldn't create workspace";
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <div className="px-3 pb-3">
      <button
        type="button"
        data-testid="sidebar-upsell-business"
        onClick={() => setOpen(true)}
        className="group relative w-full text-left rounded-lg border border-dashed border-[var(--brand)]/40 bg-gradient-to-br from-[var(--brand-light)] to-transparent p-3 hover:border-[var(--brand)] hover:from-[var(--brand-light)] transition-colors overflow-hidden"
      >
        <div className="absolute -top-3 -right-3 opacity-25 group-hover:opacity-40 transition-opacity">
          <Sparkles className="w-10 h-10 text-[var(--brand)]" />
        </div>
        <div className="flex items-center gap-2 text-[var(--brand-text)]">
          <Briefcase className="w-3.5 h-3.5 shrink-0" />
          <div className="text-[10px] uppercase tracking-widest font-medium">Accountrix · Business</div>
        </div>
        <div className="text-xs mt-1 text-[var(--text)] leading-snug">
          Running a side hustle?<br />
          <span className="font-medium">Add a Business workspace</span>
          <ArrowUpRight className="inline w-3 h-3 ml-0.5 -mt-0.5" />
        </div>
      </button>

      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-lg" data-testid="create-business-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[var(--brand)]" />
              Add a Business workspace
            </DialogTitle>
            <DialogDescription>
              Your Personal books stay untouched. We'll create a brand-new Business workspace with Invoices, Bills, Payroll, Inventory, and full statement reporting, then switch you into it. You can flip back anytime from the top-right entity picker.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Label htmlFor="ws-name">Workspace name</Label>
            <Input
              id="ws-name"
              data-testid="create-business-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Consulting LLC"
              maxLength={40}
              autoFocus
              disabled={busy}
            />
            <div className="text-[11px] text-[var(--muted-text)]">
              What you'll unlock — <span className="font-medium">Customers & Invoices · Vendors & Bills · Payroll · Inventory · Fixed Assets & Tax · Executive KPIs · Team RBAC</span>.
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              onClick={create}
              disabled={busy || !name.trim()}
              data-testid="create-business-submit"
              className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white"
            >
              {busy ? "Creating…" : "Create & switch"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
